import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { CollapseState, LocalStorageService } from '@recruitee/common';

import { AnimState } from './interfaces';

interface FilterableGroup {
  hasGroupAccess?: boolean;
  items: Array<Record<any, any> & { hasAccess?: boolean }>;
}

interface CollapsibleGroup {
  storageKey: string;
  collapsedOnDefault?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SidebarService {
  public static readonly SIDEBAR_MENU_EXPANDED_KEY: string = 'sidebar_menu_expanded';

  public sidebarMenuExpanded: BehaviorSubject<boolean> = new BehaviorSubject(
    this.localStorageService.get(SidebarService.SIDEBAR_MENU_EXPANDED_KEY, false),
  );
  public sidebarMenuExpanded$: Observable<boolean> = this.sidebarMenuExpanded.asObservable();

  constructor(private localStorageService: LocalStorageService) {}

  public filterItemsWithoutAccess<T extends FilterableGroup>(menuContent: T[]): T[] {
    return menuContent.reduce((acc, group) => {
      if (group.hasGroupAccess === false) {
        return acc;
      }
      const filteredGroup = {
        ...group,
        items: group.items.filter(item => item.hasAccess !== false),
      };

      return [...acc, filteredGroup];
    }, []);
  }

  public loadGroupsStateFromLocalStorage<T extends CollapsibleGroup>(menuContent: T[]): T[] {
    return menuContent.map(group => {
      const { collapsedOnDefault = false } = group;
      const savedState: AnimState = this.localStorageService.get(group.storageKey);
      let collapsed: boolean;
      if (typeof savedState === 'object' && savedState) {
        collapsed = savedState.animState === CollapseState.Collapsed;
      } else if (typeof savedState === 'string') {
        collapsed = savedState === CollapseState.Collapsed;
      } else {
        collapsed = collapsedOnDefault;
      }

      return {
        ...group,
        collapsedOnDefault: collapsed,
      };
    });
  }

  public getInitialExpanded(key: string): boolean {
    return this.localStorageService.get(key, true);
  }

  public saveExpandedState(key: string, value: boolean): void {
    this.localStorageService.set(key, value);
  }

  public saveGroupState(key: string, value: AnimState): void {
    this.localStorageService.set(key, value);
  }
}
